import { api } from "./api";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PlanSummary {
  id: number;
  name: string;
  description: string | null;
  statusId: number | null;
  trialDays: number;
  isDefault: boolean;
}

export interface PlanCreateRequest {
  name: string;
  description?: string | null;
  trialDays?: number;
}

export interface PlanUpdateRequest {
  name?: string | null;
  description?: string | null;
  trialDays?: number | null;
  statusId?: number | null;
}

export interface PlanFeatureItem {
  featureId: number;
  featureKey: string | null;
  featureDesc: string | null;
  isResourceCap: boolean;
  monthlyLimit: number | null;
  trialMonthlyLimit: number | null;
}

export interface FeatureAssignItem {
  featureId: number;
  monthlyLimit?: number | null;
  trialMonthlyLimit?: number | null;
}

export interface FeatureLimitUpdate {
  monthlyLimit?: number | null;
  trialMonthlyLimit?: number | null;
}

export interface FeatureLimitResponse {
  featureId: number;
  monthlyLimit: number | null;
  trialMonthlyLimit: number | null;
}

export interface AiTierResponse {
  planId: number;
  monthlyTokensPerCompany: number;
  monthlyTokensPerUser: number;
  trialMonthlyTokenLimit: number | null;
}

export interface AiTierUpdateRequest {
  monthlyTokensPerCompany?: number | null;
  monthlyTokensPerUser?: number | null;
  trialMonthlyTokenLimit?: number | null;
}

export interface SetDefaultPlanResponse {
  planId: number;
  isDefault: true;
}
export interface AiTokenCompanySummaryDto {
  companyGuid: string;
  companyName: string | null;
  limit: number | null;
  used: number;
  inputTokens: number;
  outputTokens: number;
  reservedTokens: number;
  resetsAt: string; // ISO 8601
}

export interface AiTokenAllCompaniesResponseDto {
  companies: AiTokenCompanySummaryDto[];
}

export interface AiTokenStatusDto {
  limit: number | null;
  used: number;
  inputTokens: number;
  outputTokens: number;
  reservedTokens: number;
  resetsAt: string; // ISO 8601
}

export interface AiTokenFeatureUsageDto {
  featureKey: string;
  featureName: string | null;
  featureNameEn: string | null;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  callCount: number;
}

export interface AiTokenUserUsageDetailDto {
  userGuid: string;
  userName: string | null;
  limit: number | null;
  used: number;
  inputTokens: number;
  outputTokens: number;
  reservedTokens: number;
}

export interface AiTokenCompanyDetailResponseDto {
  companyGuid: string;
  companyName: string | null;
  company: AiTokenStatusDto;
  features: AiTokenFeatureUsageDto[];
  users: AiTokenUserUsageDetailDto[];
}

// ─── Endpoints ───────────────────────────────────────────────────────────────

export const plansAdminApi = api.injectEndpoints({
  endpoints: (build) => ({
    listPlans: build.query<PlanSummary[], { active?: boolean } | void>({
      query: (arg) => ({
        url: "admin/plans",
        params: arg?.active === undefined ? undefined : { active: arg.active },
      }),
    }),

    getPlan: build.query<PlanSummary, number>({
      query: (id) => ({ url: `admin/plans/${id}` }),
    }),

    createPlan: build.mutation<PlanSummary, PlanCreateRequest>({
      query: (body) => ({ url: "admin/plans", method: "POST", body }),
    }),

    updatePlan: build.mutation<PlanSummary, { id: number; body: PlanUpdateRequest }>({
      query: ({ id, body }) => ({ url: `admin/plans/${id}`, method: "PUT", body }),
    }),

    deletePlanById: build.mutation<void, number>({
      query: (id) => ({ url: `admin/plans/${id}`, method: "DELETE" }),
    }),

    getPlanFeatures: build.query<PlanFeatureItem[], number>({
      query: (id) => ({ url: `admin/plans/${id}/features` }),
    }),

    setPlanFeatures: build.mutation<void, { id: number; items: FeatureAssignItem[] }>({
      query: ({ id, items }) => ({ url: `admin/plans/${id}/features`, method: "PUT", body: items }),
    }),

    addPlanFeature: build.mutation<void, { id: number; featureId: number; body?: FeatureLimitUpdate }>({
      query: ({ id, featureId, body }) => ({ url: `admin/plans/${id}/features/${featureId}`, method: "POST", body }),
    }),

    removePlanFeature: build.mutation<void, { id: number; featureId: number }>({
      query: ({ id, featureId }) => ({ url: `admin/plans/${id}/features/${featureId}`, method: "DELETE" }),
    }),

    setFeatureLimit: build.mutation<FeatureLimitResponse, { id: number; featureId: number; body: FeatureLimitUpdate }>({
      query: ({ id, featureId, body }) => ({ url: `admin/plans/${id}/features/${featureId}/limit`, method: "PUT", body }),
    }),

    getAiTier: build.query<AiTierResponse, number>({
      query: (id) => ({ url: `admin/plans/${id}/ai-tier` }),
      providesTags: (_result, _error, id) => [{ type: "AiTier", id }],
    }),

    upsertAiTier: build.mutation<AiTierResponse, { id: number; body: AiTierUpdateRequest }>({
      query: ({ id, body }) => ({ url: `admin/plans/${id}/ai-tier`, method: "PUT", body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "AiTier", id }],
    }),

    setDefaultPlan: build.mutation<SetDefaultPlanResponse, number>({
      query: (id) => ({ url: `admin/plans/${id}/default`, method: "PUT" }),
    }),
    getCompanyUsageDetail: build.query<AiTokenCompanyDetailResponseDto, string>({
      query: (companyGuid) => `AiQuotasAdmin/usage/${companyGuid}`,
    }),
  }),
});

export const {
  useListPlansQuery,
  useGetPlanQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDeletePlanByIdMutation,
  useGetPlanFeaturesQuery,
  useSetPlanFeaturesMutation,
  useAddPlanFeatureMutation,
  useRemovePlanFeatureMutation,
  useSetFeatureLimitMutation,
  useGetAiTierQuery,
  useUpsertAiTierMutation,
  useSetDefaultPlanMutation,
  useGetCompanyUsageDetailQuery,
} = plansAdminApi;
